import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Box,
  Card,
  Flex,
  Heading,
  Text,
  Button,
  TextField,
  Separator,
  Tabs,
  Dialog,
  Callout,
  Select,
  SegmentedControl,
  IconButton,
  VisuallyHidden,
} from "@radix-ui/themes";
import {
  Mail,
  Save,
  Eye,
  Edit3,
  Plus,
  Trash2,
  Link as LinkIcon,
  Info,
  Code2,
  Check,
  RotateCcw,
  Layers,
  Palette,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  Lock,
} from "lucide-react";
import PageTemplate from "@/components/page-template/PageTemplate";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import { emailTemplateService } from "@/services/breeze/EmailTemplateService";
import { EmailTemplate } from "@/types/breeze/EmailTemplate";
import { SkeletonPage } from "@/components/ui/Skeleton";
import { useTenantPlan } from "@/hooks/useCommonQueries";
import { RichTextEditor } from "@/components/form/RichTextEditor";
import Surface from "@/components/ui/Surface";
import CodeMirror from "@uiw/react-codemirror";
import { html as cmHtml } from "@codemirror/lang-html";
import { EditorView } from "@codemirror/view";
import { formatHtml } from "@/utils/htmlFormatter";
import { useDebounce } from "@/hooks/useDebounce";
import { useThrottledPreview } from "@/hooks/useThrottledPreview";
import { useCaretInsert } from "@/hooks/useCaretInsert";
import EmailTemplateToolbar, { SubjectVariableButton, FileThumbnail } from "./EmailTemplateToolbar";
import SendingDomainsPanel from "./SendingDomainsPanel";
import { formatFileSize } from "@/utils/fileUtils";
import { detectCustomTokens, exampleFor } from "@/utils/emailTokens";
import {
  buildShellHtml,
  type Align,
  type Radius,
  type SectionConfig,
  type SectionLayout,
  type ShellDesignConfig,
} from "./shellHtml";
import toast from "react-hot-toast";
import type { Editor as TipTapEditor } from "@tiptap/react";

const SHELL_TYPE = "_shell";


// Rendered into {{Content}} when previewing the shell. Wrapped in a dashed
// "slot" container with a clear label so the editor never confuses the
// placeholder for shell markup.
const SHELL_PREVIEW_SAMPLE_CONTENT = `
<div style="border:2px dashed #c1c4c8;background:repeating-linear-gradient(45deg,rgba(0,0,0,0.015),rgba(0,0,0,0.015) 8px,transparent 8px,transparent 16px);border-radius:8px;padding:18px 20px 22px;position:relative;">
  <div style="font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:#7d828a;font-weight:600;margin-bottom:12px;">
    ↓ {{Content}} slot — your template body renders here ↓
  </div>
  <h1 style="margin-top:0;">Sample heading</h1>
  <p>This block illustrates where each email template's body will be inserted. Anything outside the dashed border is your shell.</p>
  <p><a class="button" href="#">Action button</a></p>
  <div style="font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:#7d828a;font-weight:600;margin-top:12px;text-align:right;">
    ↑ End of slot ↑
  </div>
</div>
`.trim();
const SHELL_TEMPLATE: EmailTemplate = {
  id: -1,
  template_type: SHELL_TYPE,
  display_name: "Shell template",
  description: "HTML wrapper around every email's body",
  subject: "",
  content: "",
  is_system: false,
  is_active: true,
  locked: false,
} as EmailTemplate;

export default withTenantAdminRequired(function EmailTemplatesPage() {
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);
  const [editData, setEditData] = useState({
    subject: "",
    content: "",
  });
  const [activeTab, setActiveTab] = useState<
    "preview" | "edit" | "design" | "source" | "variables"
  >("preview");
  const [hasChanges, setHasChanges] = useState(false);
  const editorViewRef = useRef<EditorView | null>(null);
  const tipTapEditorRef = useRef<TipTapEditor | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [shellIsCustomised, setShellIsCustomised] = useState(false);
  const [defaultShellHtml, setDefaultShellHtml] = useState<string>("");
  const [shellDesignConfigJson, setShellDesignConfigJson] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [createData, setCreateData] = useState({
    template_type: "",
    subject: "",
  });
  const [showButtonHelper, setShowButtonHelper] = useState(false);
  const [buttonText, setButtonText] = useState("");
  const [buttonLink, setButtonLink] = useState("");
  const [sampleData, setSampleData] = useState<Record<string, string>>({});
  const [isListCollapsed, setIsListCollapsed] = useState(false);
  const [isPoppedOut, setIsPoppedOut] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedView: "template" | "sending-domains" =
    searchParams.get("tab") === "sending-domains" ? "sending-domains" : "template";
  const setSelectedView = (next: "template" | "sending-domains") => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "sending-domains") params.set("tab", "sending-domains");
        else params.delete("tab");
        return params;
      },
      { replace: true },
    );
  };
  const [showAddDomain, setShowAddDomain] = useState(false);
  const subjectCaret = useCaretInsert();

  const queryClient = useQueryClient();
  const { data: plan } = useTenantPlan();
  const editingLocked = plan?.is_free === true;

  const {
    data: templates,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["email-templates"],
    queryFn: () => emailTemplateService.getAllTemplates(),
  });

  // Separate system and custom templates
  const systemTemplates = templates?.filter((t) => t.is_system) || [];
  const customTemplates = templates?.filter((t) => !t.is_system) || [];

  // Auto-select the first template when templates are loaded
  useEffect(() => {
    if (templates && templates.length > 0 && !selectedTemplate) {
      handleTemplateSelect(templates[0]);
    }
  }, [templates, selectedTemplate]);

  const isShell = selectedTemplate?.template_type === SHELL_TYPE;

  const updateTemplateMutation = useMutation({
    mutationFn: async (updatedTemplate: Partial<EmailTemplate>) => {
      if (!selectedTemplate) throw new Error("No template selected");
      if (isShell) {
        const result = await emailTemplateService.updateShell(
          updatedTemplate.content ?? null,
          shellDesignConfigJson,
        );
        return { ...selectedTemplate, content: result.html } as EmailTemplate;
      }
      return emailTemplateService.updateTemplate(
        selectedTemplate.template_type,
        updatedTemplate,
      );
    },
    onSuccess: (saved) => {
      if (isShell) {
        setShellIsCustomised(!!saved.content && saved.content !== defaultShellHtml);
      } else {
        // Update the cached list entry in place instead of refetching the whole list.
        queryClient.setQueryData<EmailTemplate[] | undefined>(
          ["email-templates"],
          (prev) => prev?.map((t) => (t.template_type === saved.template_type ? saved : t)),
        );
      }
      setHasChanges(false);
      setSavedAt(performance.now());
    },
  });

  const createTemplateMutation = useMutation({
    mutationFn: async (
      newTemplate: typeof createData & { content: string },
    ) => {
      return emailTemplateService.createTemplate(newTemplate);
    },
    onSuccess: (newTemplate) => {
      queryClient.setQueryData<EmailTemplate[] | undefined>(
        ["email-templates"],
        (prev) => [...(prev ?? []), newTemplate],
      );
      setShowCreateDialog(false);
      setCreateData({ template_type: "", subject: "" });
      handleTemplateSelect(newTemplate);
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateType: string) => {
      return emailTemplateService.deleteTemplate(templateType);
    },
    onSuccess: (_void, deletedType) => {
      queryClient.setQueryData<EmailTemplate[] | undefined>(
        ["email-templates"],
        (prev) => prev?.filter((t) => t.template_type !== deletedType),
      );
      setSelectedTemplate(null);
    },
  });

  const detectedTokens = useMemo(
    () => detectCustomTokens(`${editData.subject ?? ""} ${editData.content ?? ""}`),
    [editData.subject, editData.content],
  );

  const previewArgs = useMemo(() => {
    if (!selectedTemplate) return null;
    const isShellPreview = selectedTemplate.template_type === SHELL_TYPE;
    const variables = Object.fromEntries(
      Object.entries(sampleData).filter(([, v]) => v !== ""),
    );
    return isShellPreview
      ? {
          subject: "Email shell preview",
          content: SHELL_PREVIEW_SAMPLE_CONTENT,
          wrapper_html: editData.content,
          variables,
        }
      : {
          subject: editData.subject,
          content: editData.content,
          variables,
        };
  }, [selectedTemplate, editData.subject, editData.content, sampleData]);

  const previewContent = useThrottledPreview(
    previewArgs ? JSON.stringify(previewArgs) : "",
    () => emailTemplateService.previewRaw(previewArgs!),
    !!previewArgs && activeTab === "preview",
    "",
  );

  const selectionTokenRef = useRef(0);
  const handleTemplateSelect = async (template: EmailTemplate) => {
    if (hasChanges) {
      const proceed = window.confirm(
        "You have unsaved changes. Discard them and switch template?",
      );
      if (!proceed) return;
    }
    const token = ++selectionTokenRef.current;
    setSelectedView("template");
    setSelectedTemplate(template);
    setSavedAt(null);
    setHasChanges(false);
    setActiveTab("preview");
    if (template.template_type === SHELL_TYPE) {
      try {
        const shell = await emailTemplateService.getShell();
        if (selectionTokenRef.current !== token) return;
        setEditData({ subject: "", content: shell.html });
        setShellIsCustomised(shell.is_customised);
        setDefaultShellHtml(shell.default_html);
        setShellDesignConfigJson(shell.design_config_json);
      } catch (err) {
        if (selectionTokenRef.current !== token) return;
        console.error("Failed to load shell template:", err);
        toast.error("Couldn't load the shell template. Please try again.");
      }
      return;
    }
    setEditData({ subject: template.subject, content: template.content });
  };

  const handleResetShell = async () => {
    if (!isShell) return;
    if (!confirm("Reset the shell to the platform default? Your custom shell will be replaced.")) return;
    const result = await emailTemplateService.updateShell(null, null);
    setEditData({ subject: "", content: result.html });
    setShellIsCustomised(false);
    setShellDesignConfigJson(null);
    setHasChanges(false);
    setSavedAt(performance.now());
  };

  const handleSave = () => {
    if (!selectedTemplate) return;
    updateTemplateMutation.mutate(editData);
  };

  const handleFieldChange = (field: keyof typeof editData, value: string) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    if (savedAt) setSavedAt(null);
  };

  const insertIntoActiveEditor = (text: string) => {
    if (activeTab === "source") {
      const view = editorViewRef.current;
      if (view && view.dom?.isConnected) {
        const pos = view.state.selection.main.head;
        view.dispatch({
          changes: { from: pos, insert: text },
          selection: { anchor: pos + text.length },
        });
        view.focus();
        return;
      }
      editorViewRef.current = null;
    } else if (activeTab === "edit") {
      const ed = tipTapEditorRef.current;
      if (ed && !ed.isDestroyed) {
        ed.chain().focus().insertContent(text).run();
        return;
      }
      tipTapEditorRef.current = null;
    }
    handleFieldChange("content", editData.content + text);
  };

  useEffect(() => {
    if (!savedAt) return;
    const t = setTimeout(() => setSavedAt(null), 2000);
    return () => clearTimeout(t);
  }, [savedAt]);


  const handleCreateFieldChange = (
    field: keyof typeof createData,
    value: string,
  ) => {
    setCreateData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateTemplate = () => {
    // Final validation
    if (!createData.template_type || !createData.subject) return;

    // Create with empty content - user will edit after creation
    createTemplateMutation.mutate({
      ...createData,
      content: "<p>Enter your email content here...</p>",
    });
  };

  const handleDeleteTemplate = () => {
    if (!selectedTemplate) return;
    if (selectedTemplate.is_system) {
      alert("Cannot delete system templates");
      return;
    }
    if (
      confirm(
        `Are you sure you want to delete the template "${selectedTemplate.display_name}"?`,
      )
    ) {
      deleteTemplateMutation.mutate(selectedTemplate.template_type);
    }
  };

  const handleInsertButton = () => {
    if (!buttonText || !buttonLink) return;

    // Generate centered button HTML
    const buttonHtml = `<p style="text-align: center;"><a href="${buttonLink}" class="button">${buttonText}</a></p>`;

    // Append to content - RichTextEditor will handle HTML properly
    const newContent = editData.content
      ? editData.content + "\n" + buttonHtml
      : buttonHtml;
    handleFieldChange("content", newContent);

    // Reset and close
    setButtonText("");
    setButtonLink("");
    setShowButtonHelper(false);
  };

  if (isLoading) {
    return <SkeletonPage title="Email Templates" />;
  }

  return (
    <PageTemplate
      title="Email Templates"
      description="Manage and customize your email templates"
      rightContent={
        selectedView === "sending-domains" ? (
          <Button onClick={() => setShowAddDomain(true)}>
            <Plus size={16} /> Add Domain
          </Button>
        ) : undefined
      }
    >
      <Box mx="auto">
        <Tabs.Root
          value={selectedView === "sending-domains" ? "sending-domains" : "templates"}
          onValueChange={(v) => setSelectedView(v === "sending-domains" ? "sending-domains" : "template")}
          mb="4"
        >
          <Tabs.List>
            <Tabs.Trigger value="templates">Templates</Tabs.Trigger>
            <Tabs.Trigger value="sending-domains">Sending Domains</Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>

        {selectedView === "sending-domains" ? (
          <Card size="3">
            <SendingDomainsPanel showAdd={showAddDomain} onShowAddChange={setShowAddDomain} />
          </Card>
        ) : (
        <>
        {editingLocked && (
          <Callout.Root color="amber" mb="4">
            <Callout.Icon>
              <Lock size={16} />
            </Callout.Icon>
            <Callout.Text>
              Editing email templates is not available on the free plan. Upgrade your Anythink plan to customise them.
            </Callout.Text>
          </Callout.Root>
        )}
        <Flex gap="6" direction={{ initial: "column", md: "row" }}>
          {/* Templates List */}
          {isListCollapsed ? (
            <Box style={{ width: 44, flexShrink: 0 }}>
              <IconButton
                variant="soft"
                color="gray"
                onClick={() => setIsListCollapsed(false)}
                title="Show templates list"
                style={{ width: 36, height: 36 }}
              >
                <ChevronRight size={16} />
              </IconButton>
            </Box>
          ) : (
          <Box style={{ width: "320px", flexShrink: 0 }}>
            <Flex direction="column" gap="4">
              <Flex gap="2" align="center">
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  variant="soft"
                  style={{ flex: 1 }}
                  disabled={editingLocked}
                >
                  <Plus size={16} />
                  Create New Template
                </Button>
                <IconButton
                  variant="soft"
                  color="gray"
                  onClick={() => setIsListCollapsed(true)}
                  title="Hide templates list"
                >
                  <ChevronLeft size={16} />
                </IconButton>
              </Flex>

              {/* Shell — pinned virtual entry; opens in the same editor as any other template, bound to /shell. */}
              <Box>
                <Text size="2" weight="bold" mb="2" style={{ display: "block" }}>
                  Layout
                </Text>
                <Surface
                  title="Shell template"
                  body="Wraps every email's body — header, footer, branding"
                  selected={selectedTemplate?.template_type === SHELL_TYPE}
                  onClick={() => handleTemplateSelect(SHELL_TEMPLATE)}
                />
              </Box>


              {!templates || templates.length === 0 ? (
                <Text size="2" color="gray">
                  {error ? `Error: ${error.message}` : "No templates found"}
                </Text>
              ) : (
                <>
                  {/* Custom Templates Section */}
                  {customTemplates.length > 0 && (
                    <Box>
                      <Text
                        size="2"
                        weight="bold"
                        mb="2"
                        style={{ display: "block" }}
                      >
                        Custom Templates
                      </Text>
                      <Flex direction="column" gap="2">
                        {customTemplates.map((template) => (
                          <Surface
                            key={template.id}
                            title={template.display_name}
                            body={template.description}
                            selected={selectedTemplate?.id === template.id}
                            onClick={() => handleTemplateSelect(template)}
                          />
                        ))}
                      </Flex>
                    </Box>
                  )}

                  {/* System Templates Section */}
                  {systemTemplates.length > 0 && (
                    <Box>
                      <Text
                        size="2"
                        weight="bold"
                        mb="2"
                        style={{ display: "block" }}
                      >
                        System Templates
                      </Text>
                      <Flex direction="column" gap="2">
                        {systemTemplates.map((template) => (
                          <Surface
                            key={template.id}
                            title={template.display_name}
                            body={template.description}
                            selected={selectedTemplate?.id === template.id}
                            onClick={() => handleTemplateSelect(template)}
                          />
                        ))}
                      </Flex>
                    </Box>
                  )}
                </>
              )}
            </Flex>
          </Box>
          )}

          {/* Edit Panel */}
          <Box style={{ flex: 1, minWidth: 0 }}>
            {selectedTemplate ? (
              <EditorShell isPoppedOut={isPoppedOut} onOpenChange={setIsPoppedOut}>
                <Box
                  p="4"
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  <Flex align="center" justify="between" mb="4">
                    <Flex align="center" gap="3">
                      <Heading size="4">{selectedTemplate.display_name}</Heading>
                      {isShell && (
                        <Badge color={shellIsCustomised ? "violet" : "gray"} variant="soft">
                          {shellIsCustomised ? "Customised" : "Using default"}
                        </Badge>
                      )}
                    </Flex>
                    <Flex gap="2" align="center">
                      {savedAt && (
                        <Flex align="center" gap="1" style={{ color: "var(--green-11)" }}>
                          <Check size={14} />
                          <Text size="1">Saved</Text>
                        </Flex>
                      )}
                      {isShell && shellIsCustomised && (
                        <Button
                          color="gray"
                          variant="soft"
                          onClick={handleResetShell}
                          disabled={editingLocked}
                        >
                          <RotateCcw size={16} />
                          Reset to default
                        </Button>
                      )}
                      {!isShell && !selectedTemplate.is_system && (
                        <Button
                          color="red"
                          variant="soft"
                          onClick={handleDeleteTemplate}
                          disabled={deleteTemplateMutation.isPending}
                        >
                          <Trash2 size={16} />
                          Delete
                        </Button>
                      )}
                      <Button
                        variant={hasChanges ? "solid" : "soft"}
                        onClick={handleSave}
                        disabled={
                          editingLocked || !hasChanges || updateTemplateMutation.isPending
                        }
                      >
                        <Save size={16} />
                        {updateTemplateMutation.isPending
                          ? "Saving..."
                          : "Save"}
                      </Button>
                      <IconButton
                        variant="soft"
                        color="gray"
                        onClick={() => setIsPoppedOut((v) => !v)}
                        title={isPoppedOut ? "Exit full screen" : "Open in full screen"}
                      >
                        {isPoppedOut ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                      </IconButton>
                    </Flex>
                  </Flex>

                  <Tabs.Root
                    value={activeTab}
                    onValueChange={(value) =>
                      setActiveTab(value as typeof activeTab)
                    }
                    style={{
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Tabs.List>
                      <Tabs.Trigger value="preview">
                        <Eye size={16} />
                        &nbsp;Preview
                      </Tabs.Trigger>
                      {isShell ? (
                        <Tabs.Trigger value="design">
                          <Palette size={16} />
                          &nbsp;Design
                        </Tabs.Trigger>
                      ) : (
                        <Tabs.Trigger value="edit">
                          <Edit3 size={16} />
                          &nbsp;Edit
                        </Tabs.Trigger>
                      )}
                      <Tabs.Trigger value="source">
                        <Code2 size={16} />
                        &nbsp;Source
                      </Tabs.Trigger>
                      <Tabs.Trigger value="variables">
                        <Layers size={16} />
                        &nbsp;Variables
                        {detectedTokens.length > 0 && (
                          <Badge color="gray" variant="soft" ml="1">
                            {detectedTokens.length}
                          </Badge>
                        )}
                      </Tabs.Trigger>
                    </Tabs.List>

                    <Tabs.Content
                      value="edit"
                      style={{ flex: 1, minHeight: 0, marginTop: "16px", overflow: "auto" }}
                    >
                      <Flex
                        direction="column"
                        gap="4"
                        style={{ height: "100%" }}
                      >
                        <Box>
                          <Text size="2" weight="medium" mb="2" asChild>
                            <label>Subject Line</label>
                          </Text>
                          <Flex gap="2" align="center">
                            <TextField.Root
                              ref={subjectCaret.inputRef}
                              value={editData.subject}
                              onChange={(e) =>
                                handleFieldChange("subject", e.target.value)
                              }
                              onBlur={subjectCaret.onBlur}
                              placeholder="Email subject..."
                              style={{ flex: 1 }}
                            />
                            <SubjectVariableButton
                              content={`${editData.subject} ${editData.content}`}
                              onInsert={(text) =>
                                handleFieldChange(
                                  "subject",
                                  subjectCaret.insertAtCaret(editData.subject, text),
                                )
                              }
                            />
                          </Flex>
                        </Box>

                        <Separator />

                        <Box style={{ flex: 1 }}>
                          <Flex align="center" justify="between" mb="2">
                            <Text size="2" weight="medium" asChild>
                              <label>Email Content</label>
                            </Text>
                            <Button
                              size="1"
                              variant="soft"
                              onClick={() => setShowButtonHelper(true)}
                            >
                              <LinkIcon size={14} />
                              Insert Button
                            </Button>
                          </Flex>
                          <EmailTemplateToolbar
                            content={editData.content}
                            onInsert={insertIntoActiveEditor}
                          />
                          <RichTextEditor
                            value={editData.content}
                            onChange={(value) =>
                              handleFieldChange("content", value)
                            }
                            placeholder="Write your email content here..."
                            rows={15}
                            enableMarkdown={false}
                            onEditorReady={(editor) => {
                              tipTapEditorRef.current = editor;
                            }}
                          />
                          <Callout.Root size="1" mt="2" color="blue">
                            <Callout.Icon>
                              <Info size={16} />
                            </Callout.Icon>
                            <Callout.Text>
                              <strong>Available variables:</strong>{" "}
                              <code>{`{{OrgName}}`}</code>,{" "}
                              <code>{`{{OrgId}}`}</code>,{" "}
                              <code>{`{{BaseUrl}}`}</code>,{" "}
                              <code>{`{{LoginUrl}}`}</code>,{" "}
                              <code>{`{{DashboardUrl}}`}</code>. Additional
                              variables must be provided when sending the email.
                            </Callout.Text>
                          </Callout.Root>
                        </Box>
                      </Flex>
                    </Tabs.Content>

                    <Tabs.Content
                      value="source"
                      style={{ flex: 1, minHeight: 0, marginTop: "16px", overflow: "auto" }}
                    >
                      <Flex
                        direction="column"
                        gap="4"
                        style={{ height: "100%" }}
                      >
                        <Callout.Root size="1" color="orange">
                          <Callout.Icon>
                            <Info size={16} />
                          </Callout.Icon>
                          <Callout.Text>
                            Edit the raw HTML source.{" "}
                            <strong>Built-in variables:</strong>{" "}
                            <code>{`{{OrgName}}`}</code>,{" "}
                            <code>{`{{OrgId}}`}</code>,{" "}
                            <code>{`{{BaseUrl}}`}</code>,{" "}
                            <code>{`{{LoginUrl}}`}</code>,{" "}
                            <code>{`{{DashboardUrl}}`}</code>. Custom variables
                            must be provided when sending.
                          </Callout.Text>
                        </Callout.Root>

                        <Flex align="center" justify="between" gap="2">
                          <EmailTemplateToolbar
                            content={editData.content}
                            onInsert={insertIntoActiveEditor}
                          />
                          <Button
                            type="button"
                            variant="soft"
                            size="1"
                            color="gray"
                            onClick={() => handleFieldChange("content", formatHtml(editData.content))}
                            title="Pretty-print the HTML"
                          >
                            Format
                          </Button>
                        </Flex>
                        <Box
                          style={{
                            flex: 1,
                            border: "1px solid var(--gray-6)",
                            borderRadius: "var(--radius-2)",
                            overflow: "hidden",
                            minHeight: "400px",
                          }}
                        >
                          <CodeMirror
                            value={editData.content}
                            extensions={[cmHtml(), EditorView.lineWrapping]}
                            onChange={(v) => handleFieldChange("content", v)}
                            onCreateEditor={(view) => { editorViewRef.current = view; }}
                            basicSetup={{
                              lineNumbers: true,
                              foldGutter: true,
                              highlightActiveLine: true,
                            }}
                            style={{ fontSize: 13, height: "100%" }}
                          />
                        </Box>
                      </Flex>
                    </Tabs.Content>

                    <Tabs.Content
                      value="preview"
                      style={{ flex: 1, minHeight: 0, marginTop: "16px" }}
                    >
                      {previewContent ? (
                        <iframe
                          srcDoc={previewContent}
                          sandbox=""
                          style={{
                            width: "100%",
                            height: "100%",
                            minHeight: 400,
                            border: "1px solid var(--gray-a6)",
                            borderRadius: "var(--radius-3)",
                          }}
                          title="Email Preview"
                        />
                      ) : (
                        <Flex align="center" justify="center" style={{ height: "100%" }}>
                          <Text color="gray">Loading preview...</Text>
                        </Flex>
                      )}
                    </Tabs.Content>

                    <Tabs.Content
                      value="design"
                      style={{ flex: 1, minHeight: 0, marginTop: "16px", overflow: "auto" }}
                    >
                      <ShellDesignPanel
                        initialConfigJson={shellDesignConfigJson}
                        onApply={(html, configJson) => {
                          setShellDesignConfigJson(configJson);
                          handleFieldChange("content", html);
                        }}
                      />
                    </Tabs.Content>

                    <Tabs.Content
                      value="variables"
                      style={{ flex: 1, minHeight: 0, marginTop: "16px", overflow: "auto" }}
                    >
                      <Box mb="3">
                        <Text size="2" weight="medium" style={{ display: "block" }}>
                          Sample variables for preview
                        </Text>
                        <Text size="1" color="gray" style={{ display: "block" }}>
                          Provide values to render the preview with real-looking data. These aren't saved with the template.
                        </Text>
                      </Box>
                      {detectedTokens.length === 0 ? (
                        <Text size="2" color="gray">
                          This template only uses built-in tokens
                          (<code>{`{{OrgName}}`}</code>, <code>{`{{BrandColor}}`}</code>, etc.).
                          Nothing to fill in.
                        </Text>
                      ) : (
                        <Flex direction="column" gap="2">
                          {detectedTokens.map((token) => (
                            <Flex key={token} align="center" gap="2">
                              <Box style={{ minWidth: 180 }}>
                                <code>{`{{${token}}}`}</code>
                              </Box>
                              <TextField.Root
                                size="2"
                                placeholder={`e.g. ${exampleFor(token)}`}
                                value={sampleData[token] ?? ""}
                                onChange={(e) =>
                                  setSampleData((prev) => ({ ...prev, [token]: e.target.value }))
                                }
                                style={{ flex: 1 }}
                              />
                            </Flex>
                          ))}
                        </Flex>
                      )}
                    </Tabs.Content>
                  </Tabs.Root>
                </Box>
              </EditorShell>
            ) : (
              <Card>
                <Box p="8">
                  <Flex
                    align="center"
                    justify="center"
                    direction="column"
                    gap="3"
                  >
                    <Mail size={48} color="var(--gray-9)" />
                    <Text color="gray" size="3">
                      Select a template to edit
                    </Text>
                  </Flex>
                </Box>
              </Card>
            )}
          </Box>
        </Flex>
        </>
        )}
      </Box>

      {/* Button Helper Dialog */}
      <Dialog.Root open={showButtonHelper} onOpenChange={setShowButtonHelper}>
        <Dialog.Content style={{ maxWidth: 500 }}>
          <Dialog.Title>Insert Button</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Add a centered button link. Built-in variables:{" "}
            <code>{`{{LoginUrl}}`}</code>, <code>{`{{DashboardUrl}}`}</code>,{" "}
            <code>{`{{BaseUrl}}`}</code>.
          </Dialog.Description>

          <Flex direction="column" gap="4">
            <Box>
              <Text size="2" weight="medium" mb="2" asChild>
                <label>Button Text</label>
              </Text>
              <TextField.Root
                value={buttonText}
                onChange={(e) => setButtonText(e.target.value)}
                placeholder="e.g., Confirm Email, Get Started"
                autoFocus
              />
            </Box>

            <Box>
              <Text size="2" weight="medium" mb="2" asChild>
                <label>Link URL</label>
              </Text>
              <TextField.Root
                value={buttonLink}
                onChange={(e) => setButtonLink(e.target.value)}
                placeholder="e.g., {{LoginUrl}}, {{DashboardUrl}}, https://example.com"
              />
              <Text size="1" color="gray" mt="1">
                Use built-in variables: <code>{`{{LoginUrl}}`}</code>,{" "}
                <code>{`{{DashboardUrl}}`}</code>, <code>{`{{BaseUrl}}`}</code>,
                or provide custom variables when sending the email.
              </Text>
            </Box>

            <Flex gap="3" justify="end" mt="2">
              <Dialog.Close>
                <Button variant="soft" color="gray" type="button">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                onClick={handleInsertButton}
                disabled={!buttonText || !buttonLink}
                type="button"
              >
                Insert Button
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Create Template Dialog */}
      <Dialog.Root open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <Dialog.Content style={{ maxWidth: 500 }}>
          <Dialog.Title>Create New Email Template</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Create a custom email template. You'll be able to edit the content
            after creation.
          </Dialog.Description>

          <Flex direction="column" gap="4">
            <Box>
              <Text size="2" weight="medium" mb="2" asChild>
                <label>Template Type (unique identifier)</label>
              </Text>
              <TextField.Root
                value={createData.template_type}
                onChange={(e) =>
                  handleCreateFieldChange(
                    "template_type",
                    e.target.value.toLowerCase(),
                  )
                }
                placeholder="e.g., Project Notification, Billing Reminder"
              />
            </Box>

            <Box>
              <Text size="2" weight="medium" mb="2" asChild>
                <label>Subject Line</label>
              </Text>
              <TextField.Root
                value={createData.subject}
                onChange={(e) =>
                  handleCreateFieldChange("subject", e.target.value)
                }
                placeholder="e.g., Welcome to {{OrgName}}"
              />
              <Text size="1" color="gray" mt="1">
                You can use template variables like {`{{OrgName}}`},{" "}
                {`{{LoginUrl}}`}, {`{{DashboardUrl}}`} in the subject.
              </Text>
            </Box>

            <Flex gap="3" justify="end" mt="4">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                onClick={handleCreateTemplate}
                disabled={!createData.template_type || !createData.subject}
                loading={createTemplateMutation.isPending}
              >
                {createTemplateMutation.isPending
                  ? "Creating..."
                  : "Create Template"}
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </PageTemplate>
  );
});

const DEFAULT_HEADER: SectionConfig = {
  logoFileId: null, logoName: "", logoHeightPx: 32,
  text: "{{OrgName}}", layout: "text", align: "center",
};
const DEFAULT_FOOTER: SectionConfig = {
  logoFileId: null, logoName: "", logoHeightPx: 24,
  text: "Sent by {{OrgName}} · {{BaseUrl}}", layout: "text", align: "center",
};

const DESIGN_PRESETS: { name: string; swatch: string[]; config: ShellDesignConfig }[] = [
  {
    name: "Clean light",
    swatch: ["#f6f7f9", "#ffffff", "#1f2937"],
    config: {
      background: "#f6f7f9", panel: "#ffffff", text: "#1f2937",
      buttonBg: "{{BrandColor}}", buttonText: "#ffffff",
      radius: "md", contentAlign: "left",
      header: { ...DEFAULT_HEADER },
      footer: { ...DEFAULT_FOOTER },
    },
  },
  {
    name: "Bold dark",
    swatch: ["#0f172a", "#1e293b", "#e2e8f0"],
    config: {
      background: "#0f172a", panel: "#1e293b", text: "#e2e8f0",
      buttonBg: "{{BrandColor}}", buttonText: "#0f172a",
      radius: "lg", contentAlign: "left",
      header: { ...DEFAULT_HEADER, align: "left" },
      footer: { ...DEFAULT_FOOTER, align: "left" },
    },
  },
  {
    name: "Editorial",
    swatch: ["#ffffff", "#fafafa", "#111111"],
    config: {
      background: "#ffffff", panel: "#fafafa", text: "#111111",
      buttonBg: "#111111", buttonText: "#ffffff",
      radius: "none", contentAlign: "left",
      header: { ...DEFAULT_HEADER },
      footer: { ...DEFAULT_FOOTER },
    },
  },
];

function ShellDesignPanel({
  initialConfigJson,
  onApply,
}: {
  initialConfigJson: string | null;
  onApply: (html: string, configJson: string) => void;
}) {
  const [config, setConfig] = useState<ShellDesignConfig>(() => {
    if (initialConfigJson) {
      try {
        return JSON.parse(initialConfigJson) as ShellDesignConfig;
      } catch {
        // fall through to preset
      }
    }
    return DESIGN_PRESETS[0].config;
  });
  const update = <K extends keyof ShellDesignConfig>(key: K, value: ShellDesignConfig[K]) =>
    setConfig((prev) => ({ ...prev, [key]: value }));
  const updateSection = (key: "header" | "footer", patch: Partial<SectionConfig>) =>
    setConfig((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const generatedHtml = useMemo(() => buildShellHtml(config), [config]);
  const previewHtml = useThrottledPreview(
    generatedHtml,
    () => emailTemplateService.previewRaw({
      subject: "Shell design preview",
      content: SHELL_PREVIEW_SAMPLE_CONTENT,
      wrapper_html: generatedHtml,
    }),
    true,
    "",
  );

  return (
    <Flex gap="4" align="start" wrap="wrap">
      <Box style={{ flex: "2 1 420px", minWidth: 360 }}>
        <Flex direction="column" gap="3">
          <Card>
            <Box p="3">
              <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
                Quick presets
              </Text>
              <Flex gap="2" wrap="wrap">
                {DESIGN_PRESETS.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    onClick={() => setConfig(p.config)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "8px 12px",
                      border: "1px solid var(--gray-6)",
                      borderRadius: 8, background: "var(--color-surface)",
                      cursor: "pointer",
                    }}
                  >
                    <Flex gap="0" style={{ flexShrink: 0 }}>
                      {p.swatch.map((c, i) => (
                        <span key={i} style={{
                          width: 14, height: 18, background: c,
                          borderTop: "1px solid var(--gray-6)",
                          borderBottom: "1px solid var(--gray-6)",
                          borderLeft: i === 0 ? "1px solid var(--gray-6)" : "none",
                          borderRight: i === p.swatch.length - 1 ? "1px solid var(--gray-6)" : "none",
                        }} />
                      ))}
                    </Flex>
                    <Text size="2">{p.name}</Text>
                  </button>
                ))}
              </Flex>
            </Box>
          </Card>

          <Card>
            <Box p="3">
              <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
                Theme
              </Text>
              <Flex gap="3" wrap="wrap">
                <ColorField label="Page background" value={config.background}
                  onChange={(v) => update("background", v)} />
                <ColorField label="Panel background" value={config.panel}
                  onChange={(v) => update("panel", v)} />
                <ColorField label="Text colour" value={config.text}
                  onChange={(v) => update("text", v)} />
                <ColorField label="Button background" value={config.buttonBg}
                  onChange={(v) => update("buttonBg", v)} />
                <ColorField label="Button text" value={config.buttonText}
                  onChange={(v) => update("buttonText", v)} />
                <Box style={{ minWidth: 160 }}>
                  <Text size="2" weight="medium" mb="1" style={{ display: "block" }}>
                    Corner radius
                  </Text>
                  <Select.Root value={config.radius}
                    onValueChange={(v) => update("radius", v as Radius)}>
                    <Select.Trigger style={{ width: "100%" }} />
                    <Select.Content position="popper">
                      <Select.Item value="none">None</Select.Item>
                      <Select.Item value="sm">Small</Select.Item>
                      <Select.Item value="md">Medium</Select.Item>
                      <Select.Item value="lg">Large</Select.Item>
                    </Select.Content>
                  </Select.Root>
                </Box>
              </Flex>
              <Box mt="3">
                <AlignmentRow label="Content body" value={config.contentAlign}
                  onChange={(v) => update("contentAlign", v)} />
              </Box>
            </Box>
          </Card>

          <SectionCard title="Header" config={config.header}
            onPatch={(patch) => updateSection("header", patch)} />
          <SectionCard title="Footer" config={config.footer}
            onPatch={(patch) => updateSection("footer", patch)} />

          <Callout.Root size="1" color="orange">
            <Callout.Icon><Info size={14} /></Callout.Icon>
            <Callout.Text>
              Applying overwrites the shell HTML in the Source tab. Save the template to persist.
            </Callout.Text>
          </Callout.Root>

          <Flex justify="end">
            <Button
              type="button"
              onClick={() => {
                const proceed = window.confirm(
                  "Apply this design? It will overwrite the current shell HTML in the Source tab.",
                );
                if (proceed) onApply(generatedHtml, JSON.stringify(config));
              }}
            >
              Apply design to shell
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Box style={{
        flex: "1 1 320px", minWidth: 280, position: "sticky", top: 8,
      }}>
        <Card>
          <Box p="3">
            <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
              Live preview
            </Text>
            <Box style={{
              border: "1px solid var(--gray-5)", borderRadius: 6, overflow: "hidden",
              height: "calc(100vh - 200px)", minHeight: 460, background: "white",
            }}>
              {previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  sandbox=""
                  title="Shell design preview"
                  style={{ width: "100%", height: "100%", border: 0 }}
                />
              ) : (
                <Flex align="center" justify="center" style={{ height: "100%" }}>
                  <Text size="2" color="gray">Rendering…</Text>
                </Flex>
              )}
            </Box>
          </Box>
        </Card>
      </Box>
    </Flex>
  );
}

/**
 * Renders the editor Card inline by default. When `isPoppedOut`, the Card is
 * the body of a Radix Dialog so we get focus trap, Esc-to-close, scroll lock,
 * click-outside, and aria-modal for free. Replaces the previous hand-rolled
 * fixed-position overlay + manual keydown listener.
 */
function EditorShell({
  isPoppedOut,
  onOpenChange,
  children,
}: {
  isPoppedOut: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const cardElement = (
    <Card
      style={
        isPoppedOut
          ? { height: "100%", display: "flex", flexDirection: "column" }
          : { height: "100%" }
      }
    >
      {children}
    </Card>
  );

  if (!isPoppedOut) return cardElement;

  return (
    <Dialog.Root open={isPoppedOut} onOpenChange={onOpenChange}>
      <Dialog.Content
        style={{
          maxWidth: "min(1600px, calc(100vw - 32px))",
          width: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 32px)",
          height: "calc(100vh - 32px)",
          padding: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <VisuallyHidden>
          <Dialog.Title>Edit email template</Dialog.Title>
        </VisuallyHidden>
        {cardElement}
      </Dialog.Content>
    </Dialog.Root>
  );
}

function SectionCard({
  title, config, onPatch,
}: { title: string; config: SectionConfig; onPatch: (patch: Partial<SectionConfig>) => void }) {
  return (
    <Card>
      <Box p="3">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>{title}</Text>
        <Flex direction="column" gap="3">
          <Box>
            <Text size="1" color="gray" mb="1" style={{ display: "block" }}>Layout</Text>
            <SegmentedControl.Root value={config.layout}
              onValueChange={(v) => onPatch({ layout: v as SectionLayout })}>
              <SegmentedControl.Item value="text">Text only</SegmentedControl.Item>
              <SegmentedControl.Item value="logo">Logo only</SegmentedControl.Item>
              <SegmentedControl.Item value="stacked">Logo + text (stacked)</SegmentedControl.Item>
              <SegmentedControl.Item value="side_by_side">Logo + text (side by side)</SegmentedControl.Item>
            </SegmentedControl.Root>
          </Box>

          {(config.layout === "logo" || config.layout === "stacked" || config.layout === "side_by_side") && (
            <LogoPicker
              fileId={config.logoFileId}
              name={config.logoName}
              heightPx={config.logoHeightPx}
              onChange={(next) => onPatch(next)}
            />
          )}

          {(config.layout === "text" || config.layout === "stacked" || config.layout === "side_by_side") && (
            <Box>
              <Text size="1" color="gray" mb="1" style={{ display: "block" }}>Text</Text>
              <TextField.Root
                size="2"
                value={config.text}
                onChange={(e) => onPatch({ text: e.target.value })}
                placeholder="e.g. {{OrgName}} or a custom tagline"
              />
              <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
                Tokens like <code>{`{{OrgName}}`}</code>, <code>{`{{BaseUrl}}`}</code> are substituted at send time.
              </Text>
            </Box>
          )}

          <AlignmentRow label="Alignment" value={config.align}
            onChange={(v) => onPatch({ align: v })} />
        </Flex>
      </Box>
    </Card>
  );
}

function LogoPicker({
  fileId, name, heightPx, onChange,
}: {
  fileId: number | null;
  name: string;
  heightPx: number;
  onChange: (patch: Partial<SectionConfig>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 250);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  const { data: pickerData } = useQuery({
    queryKey: ["any-files-logo-picker", debounced],
    queryFn: async () => {
      const { fileService } = await import("@/services/breeze/FileService");
      return fileService.getAll(1, 25, undefined, "asc", debounced || undefined, "image");
    },
    enabled: open,
  });
  const items: any[] = (pickerData as any)?.items ?? (Array.isArray(pickerData) ? pickerData : []);

  const { data: currentUrl } = useQuery({
    queryKey: ["file-signed-url", fileId],
    queryFn: async () => {
      const { fileService } = await import("@/services/breeze/FileService");
      return fileService.getFileSignedUrl(fileId!);
    },
    enabled: !!fileId,
    staleTime: 4 * 60 * 1000,
  });

  const choose = (id: number, originalName: string) => {
    const safeName = (originalName || `file-${id}`).replace(/[^A-Za-z0-9._-]/g, "_");
    onChange({ logoFileId: id, logoName: safeName });
    setOpen(false);
  };

  const clear = () => onChange({ logoFileId: null, logoName: "" });

  const handleUpload = async (file: globalThis.File) => {
    setUploading(true);
    try {
      const { fileService } = await import("@/services/breeze/FileService");
      const formData = new FormData();
      formData.append("file", file);
      const uploaded = await fileService.create(formData);
      queryClient.invalidateQueries({ queryKey: ["any-files-logo-picker"] });
      choose(uploaded.id, uploaded.original_file_name || `file-${uploaded.id}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Text size="1" color="gray" mb="1" style={{ display: "block" }}>Logo</Text>
      <Flex gap="2" align="center">
        <Box style={{
          width: 64, height: 48, border: "1px solid var(--gray-6)",
          borderRadius: 6, background: "var(--gray-2)",
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden",
        }}>
          {fileId && currentUrl ? (
            <img src={currentUrl} alt="Logo"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
          ) : (
            <Text size="1" color="gray">No logo</Text>
          )}
        </Box>
        <Box style={{ flex: 1 }}>
          <Flex gap="2" wrap="wrap">
            <Button type="button" size="2" variant="soft"
              onClick={() => setOpen((v) => !v)}>
              {fileId ? "Change" : "Choose from library"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
                e.target.value = "";
              }}
            />
            <Button type="button" size="2" variant="soft" color="gray"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}>
              {uploading ? "Uploading…" : "Upload new"}
            </Button>
            {fileId && (
              <Button type="button" size="2" variant="ghost" color="gray" onClick={clear}>
                Remove
              </Button>
            )}
          </Flex>
        </Box>
      </Flex>

      {open && (
        <Box mt="2" style={{
          border: "1px solid var(--gray-5)", borderRadius: 6,
          maxHeight: 240, overflow: "auto",
        }}>
          <Box p="2">
            <TextField.Root size="1" placeholder="Search images…"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </Box>
          {items.length === 0 ? (
            <Box p="3"><Text size="1" color="gray">No images found.</Text></Box>
          ) : (
            items.map((f: any) => {
              const name = f.original_file_name || `file-${f.id}`;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => choose(f.id, name)}
                  style={{
                    display: "flex", gap: 10, alignItems: "center",
                    padding: "8px 10px", width: "100%", textAlign: "left",
                    background: "transparent", border: "none",
                    borderTop: "1px solid var(--gray-3)", cursor: "pointer",
                  }}
                >
                  <FileThumbnail fileId={f.id} alt={name} />
                  <Box style={{ minWidth: 0, flex: 1 }}>
                    <Text size="2" weight="medium" style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>{name}</Text>
                    {f.file_size != null && (
                      <Text size="1" color="gray">
                        {formatFileSize(f.file_size)}
                      </Text>
                    )}
                  </Box>
                </button>
              );
            })
          )}
        </Box>
      )}

      <Flex align="center" gap="2" mt="2">
        <Text size="1" color="gray" style={{ minWidth: 90 }}>Logo height</Text>
        <input
          type="range" min={16} max={120} value={heightPx}
          onChange={(e) => onChange({ logoHeightPx: Number(e.target.value) })}
          style={{ flex: 1 }}
        />
        <Text size="1" color="gray" style={{ minWidth: 40 }}>{heightPx}px</Text>
      </Flex>

      <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
        Logo file: {name || "—"}
      </Text>
    </Box>
  );
}

function ColorField({
  label, value, onChange,
}: { label: string; value: string; onChange: (next: string) => void }) {
  const isHex = /^#[0-9a-f]{3,8}$/i.test(value);
  return (
    <Box style={{ minWidth: 200 }}>
      <Text size="2" weight="medium" mb="1" style={{ display: "block" }}>{label}</Text>
      <Flex gap="2" align="center">
        <input
          type="color"
          value={isHex ? value : "#888888"}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: 36, height: 32, padding: 0, border: "1px solid var(--gray-6)",
            borderRadius: 6, background: "transparent",
          }}
          aria-label={`${label} colour picker`}
        />
        <TextField.Root size="2" value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#ffffff or {{BrandColor}}"
          style={{ flex: 1 }} />
      </Flex>
    </Box>
  );
}

function AlignmentRow({
  label, value, onChange,
}: { label: string; value: Align; onChange: (next: Align) => void }) {
  return (
    <Flex align="center" gap="3">
      <Box style={{ minWidth: 100 }}>
        <Text size="2" weight="medium">{label}</Text>
      </Box>
      <SegmentedControl.Root value={value}
        onValueChange={(v) => onChange(v as Align)}>
        <SegmentedControl.Item value="left">Left</SegmentedControl.Item>
        <SegmentedControl.Item value="center">Centre</SegmentedControl.Item>
        <SegmentedControl.Item value="right">Right</SegmentedControl.Item>
      </SegmentedControl.Root>
    </Flex>
  );
}

